import { PLAYER, wrapAngle } from "@tiny-seas/shared";
import { settings } from "./settings.ts";

/**
 * Keyboard + mouse state. Gameplay input is only live while the pointer is
 * locked and no overlay is open; otherwise movement reads as zero.
 */

export type ActionKey = "interact" | "drop" | "map" | "players" | "crewMenu" | "sailUp" | "sailDown" | "voteYes" | "voteNo" | "equipSword" | "equipShovel" | "equipSpyglass" | "equipPistol" | "reload" | "loadSelf" | "toggleView";

const ACTION_CODES: Record<string, ActionKey> = {
  KeyE: "interact",
  KeyF: "loadSelf",
  KeyQ: "drop",
  KeyM: "map",
  KeyY: "voteYes",
  KeyN: "voteNo",
  Digit1: "equipSword",
  Digit2: "equipShovel",
  Digit3: "equipSpyglass",
  Digit4: "equipPistol",
  KeyR: "reload",
  KeyV: "toggleView",
};

export class Input {
  /** Look angles in the frame of the player's body (world or ship). */
  yaw = 0;
  pitch = 0;
  private readonly down = new Set<string>();
  private readonly pressed: ActionKey[] = [];
  private released = false;
  private primaryQueued = false;
  private blockHeld = false;
  private lookScale = 1;
  locked = false;
  /** Browser automation only: treat input as captured without pointer lock. */
  forceActive = false;
  /** An overlay (map, dialog) suppresses gameplay input without unlocking. */
  private overlayOpen = false;
  private lookAccum = 0;
  onLockChange: (locked: boolean) => void = () => {};
  onClear: () => void = () => {};
  private lockRequestedAt = 0;
  private readonly canvas: HTMLCanvasElement;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    window.addEventListener("keydown", (e) => this.onKey(e, true));
    window.addEventListener("keyup", (e) => this.onKey(e, false));
    window.addEventListener("blur", () => this.clear());
    document.addEventListener("pointerlockchange", () => {
      this.locked = document.pointerLockElement === canvas;
      if (!this.locked) this.clear();
      this.onLockChange(this.locked);
    });
    document.addEventListener("mousemove", (e) => {
      if (!this.locked || this.overlay) return;
      const k = 0.0022 * settings.value.sensitivity * this.lookScale;
      this.yaw = wrapAngle(this.yaw + e.movementX * k);
      this.pitch = Math.max(-PLAYER.maxPitch, Math.min(PLAYER.maxPitch, this.pitch + e.movementY * k * (settings.value.invertY ? -1 : 1)));
      this.lookAccum += Math.abs(e.movementX) + Math.abs(e.movementY);
    });
    // Chorded button changes arrive as pointermove; ordinary motion has button -1.
    // Babylon cancels pointerdown, suppressing compatibility mousedown events.
    const pointerButton = (e: PointerEvent): void => {
      if (!this.active) return;
      if (e.button === 0 && (e.buttons & 1) !== 0) this.primaryQueued = true;
      if (e.button === 2) this.blockHeld = (e.buttons & 2) !== 0;
    };
    canvas.addEventListener("pointerdown", pointerButton);
    canvas.addEventListener("pointermove", pointerButton);
    window.addEventListener("mouseup", (e) => {
      if (e.button === 2) this.blockHeld = false;
    });
    window.addEventListener("pointermove", (e) => {
      if (e.button === 2 && (e.buttons & 2) === 0) this.blockHeld = false;
    });
    window.addEventListener("pointerup", (e) => {
      if (e.button === 2) this.blockHeld = false;
    });
    window.addEventListener("pointercancel", () => {
      this.blockHeld = false;
    });
    canvas.addEventListener("contextmenu", (e) => {
      if (this.active) e.preventDefault();
    });
  }

  /** Requests pointer lock; resolves false if the browser refused (e.g. re-lock cooldown). */
  async lock(): Promise<boolean> {
    this.lockRequestedAt = performance.now();
    try {
      await this.canvas.requestPointerLock();
      return true;
    } catch {
      return false;
    }
  }

  get lockRequestRecent(): boolean {
    return performance.now() - this.lockRequestedAt < 1500;
  }
  get overlay(): boolean {
    return this.overlayOpen;
  }

  set overlay(open: boolean) {
    if (open && !this.overlayOpen) this.clear();
    this.overlayOpen = open;
  }

  /** Contextual secondary intent; never survives overlays or lost capture. */
  isSecondaryHeld(): boolean {
    return this.active && this.blockHeld;
  }

  setLookScale(scale: number): void {
    this.lookScale = Math.max(0.25, Math.min(1, scale));
  }

  get active(): boolean {
    return (this.locked || this.forceActive) && !this.overlay;
  }

  private onKey(e: KeyboardEvent, isDown: boolean): void {
    const target = e.target;
    if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return;
    if (isDown) {
      if ((e.code === "KeyM" || e.code === "KeyP") && !e.repeat) {
        this.pressed.push(e.code === "KeyM" ? "map" : "players");
        e.preventDefault();
        return;
      }
      // Spectators often have no pointer lock; J (crew picker) works whenever no dialog is open.
      if (e.code === "KeyJ" && !e.repeat && !this.overlay) {
        this.pressed.push("crewMenu");
        return;
      }
      if (!this.active) return;
      if (["Space", "KeyW", "KeyA", "KeyS", "KeyD", "KeyC"].includes(e.code)) e.preventDefault();
      if (!e.repeat) {
        const action = ACTION_CODES[e.code];
        if (action) this.pressed.push(action);
        if (e.code === "KeyW") this.pressed.push("sailUp");
        if (e.code === "KeyS") this.pressed.push("sailDown");
      }
      this.down.add(e.code);
    } else {
      if (e.code === "KeyE") this.released = true;
      this.down.delete(e.code);
    }
  }

  /** Clears held intent on capture, overlay, connection and life-state boundaries. */
  clear(): void {
    this.lookScale = 1;
    this.onClear();
    if (this.down.has("KeyE")) this.released = true;
    this.down.clear();
    this.primaryQueued = false;
    this.blockHeld = false;
    this.pressed.length = 0;
  }

  isDown(code: string): boolean {
    return this.active && this.down.has(code);
  }

  axis(neg: string, pos: string): number {
    return (this.isDown(pos) ? 1 : 0) - (this.isDown(neg) ? 1 : 0);
  }

  /** Drains one-shot key presses since the last call. */
  takePressed(): ActionKey[] {
    const out = this.pressed.slice();
    this.pressed.length = 0;
    return out;
  }

  takeInteractReleased(): boolean {
    const r = this.released;
    this.released = false;
    return r;
  }

  takePrimary(): boolean {
    const primary = this.primaryQueued;
    this.primaryQueued = false;
    return primary && this.active;
  }

  /** Total mouse travel since the last call (for the onboarding "look" step). */
  takeLook(): number {
    const v = this.lookAccum;
    this.lookAccum = 0;
    return v;
  }
}
